<script lang="ts">
  import DownloadIcon from "../utils/assets/download_icon.svelte";
  import ClipLoader from "../utils/assets/clip_loader.svelte";
  import { getFileTypeIcon, downloadFile } from "../utils/functions";
  import {
    attachmentModalOpen,
    attachmentModalInitialIndex,
    attachments as attachmentsStore,
    attachmentModalTimestamp,
  } from "../stores/chat";

  export let hasMessage: boolean = false;
  export let attachments: {
    file_url: string;
    file_name?: string;
    content_type?: string;
    size_in_bytes?: number;
  }[] = [];
  export let timestamp: string = "";

  let isDownloadingAll = false;
  let downloadingFiles: Record<number, boolean> = {};

  $: images = attachments?.filter((a) => a?.content_type?.startsWith("image/"));
  $: files = attachments?.filter((a) => !a?.content_type?.startsWith("image/"));
  $: visibleImages = images?.slice(0, 4);
  $: remainingCount = images?.length - 4;

  $: imagesBorderRadius =
    images?.length > 0 && files?.length > 0 && hasMessage
      ? "0px"
      : images?.length > 0 && files?.length > 0 && !hasMessage
        ? "8px 8px 0px 0px"
        : images?.length > 0 && files?.length === 0 && hasMessage
          ? "0px 0px 8px 8px"
          : images?.length > 0 && files?.length === 0 && !hasMessage
            ? "8px"
            : "8px";

  $: fileBorderRadius =
    (images?.length > 0 && files?.length > 0) ||
    (images?.length === 0 && files?.length > 0 && hasMessage)
      ? "0 0 8px 8px"
      : "8px";

  $: imageGridHeight = images?.length >= 3 ? "196px" : "150px";

  const downloadAllImages = async () => {
    isDownloadingAll = true;
    try {
      await Promise?.allSettled(
        (images || [])
          ?.filter((image) => image?.file_url)
          ?.map((image) => downloadFile(image?.file_url, image?.file_name))
      );
    } finally {
      isDownloadingAll = false;
    }
  };

  const handleFileDownload = async (fileUrl: string, fileName: string | undefined, fileIndex: number) => {
    downloadingFiles[fileIndex] = true;
    downloadingFiles = { ...downloadingFiles };
    try {
      await downloadFile(fileUrl, fileName);
    } finally {
      downloadingFiles[fileIndex] = false;
      downloadingFiles = { ...downloadingFiles };
    }
  };

  const openModal = (index: number) => {
    attachmentsStore.set(attachments);
    attachmentModalInitialIndex.set(index);
    attachmentModalOpen.set(true);
    attachmentModalTimestamp.set(timestamp);
  };
</script>

<div class="verbaflo-attachment-container">
  {#if images?.length > 0}
    <div class="verbaflo-attachment-image-container">
      <div
        class="verbaflo-attachment-image-grid"
        style="--images-border-radius: {imagesBorderRadius}; --image-grid-height: {imageGridHeight};"
        class:verbaflo-attachment-single={images?.length === 1}
        class:verbaflo-attachment-pair={images?.length === 2}
        class:verbaflo-attachment-triple={images?.length === 3}
        class:verbaflo-attachment-four={images?.length === 4 ||
          images?.length > 4}
      >
        {#each visibleImages as image, i}
          <div
            class="verbaflo-attachment-image-wrapper"
            class:verbaflo-attachment-has-overlay={i === 3 &&
              remainingCount > 0}
            on:click={() => openModal(i)}
            on:keydown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                openModal(i);
              }
            }}
            role="button"
            tabindex="0"
          >
            <img
              src={image?.file_url}
              alt={image?.file_name}
              class="verbaflo-attachment-image"
            />
            {#if i === 3 && remainingCount > 0}
              <div
                class="verbaflo-attachment-overlay"
                on:click|stopPropagation={() => openModal(3)}
                on:keydown|stopPropagation={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    openModal(3);
                  }
                }}
                role="button"
                tabindex="0"
              >
                +{remainingCount + 1}
              </div>
            {/if}
          </div>
        {/each}
        <button
          type="button"
          class="verbaflo-attachment-download-btn"
          on:click={downloadAllImages}
          aria-label="Download all images"
          disabled={isDownloadingAll}
        >
          {#if isDownloadingAll}
            <ClipLoader size={18} thickness={2} speed={1} color="#051133" />
          {:else}
            <DownloadIcon size="18" />
          {/if}
        </button>
      </div>
    </div>
  {/if}

  {#if files?.length > 0}
    <div class="verbaflo-attachment-document-container">
      {#each files as file, fileIndex}
        {@const fileIconUrl =
          getFileTypeIcon(file?.file_name, file?.content_type) ??
          "https://vf-assets.verbaflo.com/vf-email/vf-email/doc.svg"}
        {@const attachmentIndex = images?.length + fileIndex}
        <div
          class="verbaflo-attachment-file-card"
          style="--file-border-radius: {fileBorderRadius};"
        >
          <div
            class="verbaflo-attachment-file-info"
            on:click|stopPropagation={() => openModal(attachmentIndex)}
            on:keydown|stopPropagation={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                openModal(attachmentIndex);
              }
            }}
            role="button"
            tabindex="0"
          >
            <img
              src={fileIconUrl}
              alt={file?.file_name || "Attachment"}
              class="verbaflo-attachment-file-icon"
            />
            <span class="verbaflo-attachment-file-name">
              {file?.file_name || "Attachment"}
            </span>
          </div>
          <div class="verbaflo-attachment-divider" />
          <button
            class="verbaflo-attachment-download-icon"
            on:click|stopPropagation={() =>
              handleFileDownload(file.file_url, file.file_name, fileIndex)}
            disabled={downloadingFiles[fileIndex]}
          >
            {#if downloadingFiles[fileIndex]}
              <ClipLoader size={18} thickness={2} speed={1.5} color="#051133" />
            {:else}
              <DownloadIcon size="18" />
            {/if}
          </button>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  :global(#vfwidget .verbaflo-attachment-container) {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
    width: 100% !important;
    min-width: 280px !important;
  }

  :global(#vfwidget .verbaflo-attachment-document-container) {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
  }

  :global(#vfwidget .verbaflo-attachment-file-card:hover) {
    background: #ececec !important;
  }

  :global(#vfwidget .verbaflo-attachment-image-container) {
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-attachment-file-card) {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    background: #f6f6f6 !important;
    border-radius: var(--file-border-radius, 8px) !important;
    padding: 12px !important;
    gap: 10px !important;
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-attachment-file-info) {
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
  }

  :global(#vfwidget .verbaflo-attachment-file-name) {
    font-size: 14px !important;
    color: #051133 !important;
    font-weight: 400 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    line-clamp: 2 !important;
    -webkit-line-clamp: 2 !important;
  }

  :global(#vfwidget .verbaflo-attachment-download-btn) {
    all: unset !important;
    position: absolute !important;
    right: -30px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
  }

  :global(#vfwidget .verbaflo-attachment-image-grid) {
    position: relative !important;
    display: grid !important;
    gap: 4px !important;
    background: #f6f6f6 !important;
    border-radius: var(--images-border-radius, 8px) !important;
    padding: 4px !important;
    height: var(--image-grid-height, 145px) !important;
  }

  :global(#vfwidget .verbaflo-attachment-single) {
    grid-template-columns: 1fr !important;
  }

  :global(#vfwidget .verbaflo-attachment-pair) {
    grid-template-columns: 1fr 1fr !important;
    grid-template-rows: 1fr !important;
  }

  :global(#vfwidget .verbaflo-attachment-triple) {
    grid-template-columns: 1fr 1fr !important;
    grid-template-rows: 1fr 1fr !important;
  }

  :global(#vfwidget .verbaflo-attachment-four) {
    grid-template-columns: 1fr 1fr !important;
    grid-template-rows: 1fr 1fr !important;
  }

  :global(
      #vfwidget
        .verbaflo-attachment-triple
        .verbaflo-attachment-image-wrapper:first-child
    ) {
    grid-row: 1 / 3 !important;
  }

  :global(#vfwidget .verbaflo-attachment-image-wrapper) {
    position: relative !important;
    overflow: hidden !important;
    border-radius: 4px !important;
    width: 100% !important;
    height: 100% !important;
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-attachment-image) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    display: block !important;
  }

  :global(#vfwidget .verbaflo-attachment-overlay) {
    position: absolute !important;
    inset: 0 !important;
    background: rgba(0, 0, 0, 0.5) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    color: white !important;
    font-size: 18px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-attachment-divider) {
    width: 1px !important;
    height: 14px !important;
    background: #ffffff !important;
    margin-left: auto !important;
  }

  :global(#vfwidget svg) {
    flex-shrink: 0 !important;
  }

  :global(#vfwidget .verbaflo-attachment-download-icon) {
    all: unset !important;
    cursor: pointer !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :global(#vfwidget .verbaflo-attachment-download-btn) {
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-attachment-file-icon) {
    width: 28px !important;
    height: 28px !important;
    object-fit: contain !important;
    flex-shrink: 0 !important;
  }
</style>
